// ── PUSD (ParyonUSD) CashToken constants & helpers ────────────────
//
// PUSD is a dollar-pegged stablecoin issued as a fungible CashToken on
// Bitcoin Cash mainnet. Because it tracks $1, a USD-denominated tip maps
// almost 1:1 onto a PUSD amount — no BCH price feed is required.
//
// Verify the category ID against a canonical source before trusting it:
//   https://paryonusd.com  ·  https://bcmr.paytaca.com
//
// Scams impersonate stablecoins by minting look-alike tokens with a
// different category. ALWAYS compare against this constant.

/** PUSD genesis category ID (32-byte hex). The on-chain identity of the token. */
export const PUSD_CATEGORY = '2469acc5afa4b10cb5b5c04afb89c3a3ffd61c5da9c01e26d00951cae2a02544';

/** PUSD has 2 decimal places — the smallest base unit is 0.01 PUSD (one cent). */
export const PUSD_DECIMALS = 2;

/** One whole PUSD expressed in base units (10^decimals). */
export const PUSD_UNIT = BigInt(10) ** BigInt(PUSD_DECIMALS); // 100n

/** Ticker / display symbol. */
export const PUSD_SYMBOL = 'PUSD';

/**
 * Satoshi value attached to each token UTXO we create. CashToken p2pkh
 * outputs have an 800-sat dust floor; mainnet-js defaults to 1000. We use
 * 1000 explicitly so a PUSD tip always carries enough BCH to be spendable.
 */
export const PUSD_OUTPUT_SATS = BigInt(1000);

/**
 * Convert a human PUSD amount (e.g. 0.25) to base units (e.g. 25n).
 * Rounds to the nearest cent — PUSD cannot represent fractions of a cent.
 */
export function pusdToBaseUnits(amount: number): bigint {
  if (!Number.isFinite(amount) || amount <= 0) return BigInt(0);
  return BigInt(Math.round(amount * Number(PUSD_UNIT)));
}

/**
 * Convert PUSD base units (e.g. 25n) back to a human amount (e.g. 0.25).
 */
export function baseUnitsToPusd(base: bigint): number {
  return Number(base) / Number(PUSD_UNIT);
}

/** Format a PUSD base-unit amount for display, e.g. 2550n → "$25.50". */
export function formatPusd(base: bigint): string {
  return `$${baseUnitsToPusd(base).toFixed(2)}`;
}
